<template>
  <div class="idi-chart-page" :class="{ 'idi-chart-page--embedded': embedded }">
    <header class="idi-chart__header">
      <div class="idi-chart__title-group">
        <div class="idi-chart__title">
          <span class="symbol">BTC/USDT</span>
          <span class="tf-tag">Binance 7d</span>
          <span class="tf-tag">MEXC ao vivo</span>
          <span class="tf-tag">15m</span>
          <span class="chart-type-tag">Velas / Candlestick</span>
        </div>
        <div class="idi-chart__price-display" v-if="effectivePrice">
          <span class="lbl">Preço:</span>
          <span class="val">${{ Math.round(effectivePrice).toLocaleString('en-US') }}</span>
        </div>
      </div>

      <PortfolioToolbar v-if="hasModule('portfolio')" />

      <div class="idi-chart__controls">
        <div ref="profileToolbarHost" class="idi-chart__profile-slot" />
        <button
          class="idi-btn idi-btn--ws"
          :class="{ 'idi-btn--ws-active': isWsConnected }"
          @click="toggleWsConnection"
          title="Conectar/Desconectar Stream de Preço em Tempo Real (WebSocket)"
        >
          <span class="ws-dot"></span>
          <span>{{ isWsConnected ? 'WS Ao Vivo' : 'WS Pausado' }}</span>
        </button>
      </div>
    </header>

    <div class="idi-chart__wrapper">
      <div ref="containerRef" class="idi-chart__canvas" />
      <InventoryProfile
        v-if="hasModule('inventory')"
        :price-to-y="priceToY"
        :ready="chartReady"
        :toolbar-target="profileToolbarHost"
      />
      <HoverSim
        v-if="hasModule('hover')"
        :price-to-y="priceToY"
        :hover-price="hoverPrice"
        :current-price="effectivePrice"
        :ready="chartReady"
        :toolbar-target="profileToolbarHost"
      />
      <DesovaOrders
        v-if="hasModule('desova')"
        :price-to-y="priceToY"
        :current-price="effectivePrice"
        :ready="chartReady"
        :toolbar-target="profileToolbarHost"
      />
      <OpenLimits
        v-if="hasModule('limits')"
        :price-to-y="priceToY"
        :y-to-price="yToPrice"
        :ready="chartReady"
        :toolbar-target="profileToolbarHost"
      />
      <BasisTargets
        v-if="hasModule('basis')"
        :price-to-y="priceToY"
        :current-price="effectivePrice"
        :ready="chartReady"
        :toolbar-target="profileToolbarHost"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { useCandleChart } from '@/composables/useCandleChart'
import { InventoryProfile } from '@/components/inventory-profile'
import { HoverSim } from '@/components/hover-sim'
import { DesovaOrders } from '@/components/desova-orders'
import { OpenLimits } from '@/components/open-limits'
import { BasisTargets } from '@/components/basis-targets'
import { PortfolioToolbar, usePortfolioToolbar } from '@/components/portfolio-toolbar'

// eslint-disable-next-line no-undef -- compiler macro (script setup)
const props = defineProps({
  embedded: { type: Boolean, default: false },
  modules: {
    type: Array,
    default: () => ['portfolio', 'inventory', 'hover', 'desova'],
  },
})

function hasModule(id) {
  return props.modules.includes(id)
}

const store = useStore()
const containerRef = ref(null)
const profileToolbarHost = ref(null)
const chartReady = ref(false)

const { pmp: currentPmp, hedgeAvg: currentHedgeAvg } = usePortfolioToolbar()

const {
  series,
  currentPrice: chartPrice,
  hoverPrice,
  isWsConnected,
  init: initChart,
  destroy: destroyChart,
  connectWebSocket,
  disconnectWebSocket,
  updatePmpLine,
  updateHedgeLine,
} = useCandleChart(containerRef)

const storeBtcPrice = computed(
  () => store.state.marketData?.btcUsd || store.state.marketData?.price || 0
)
const effectivePrice = computed(() => chartPrice.value || storeBtcPrice.value || 0)

function priceToY(price) {
  return series.value?.priceToCoordinate(price) ?? null
}

function yToPrice(y) {
  const price = series.value?.coordinateToPrice(y)
  return price == null ? null : price
}

function toggleWsConnection() {
  if (isWsConnected.value) {
    disconnectWebSocket()
  } else {
    connectWebSocket('BTCUSDT', '15m')
  }
}

const showPortfolio = computed(() => hasModule('portfolio'))

watch(currentPmp, val => {
  if (showPortfolio.value) updatePmpLine(val)
})
watch(currentHedgeAvg, val => {
  if (showPortfolio.value) updateHedgeLine(val)
})

onMounted(async () => {
  await initChart(
    showPortfolio.value ? currentPmp.value : 0,
    showPortfolio.value ? currentHedgeAvg.value : 0,
    true,
  )
  chartReady.value = true
})

onUnmounted(() => {
  destroyChart()
})
</script>

<style scoped>
.idi-chart-page {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: calc(100vh - 40px);
  padding: 12px;
  box-sizing: border-box;
  background-color: #0d1117;
  color: #c9d1d9;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}

.idi-chart-page--embedded {
  height: 100%;
  padding: 0;
}

.idi-chart__header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px;
  background: #161b22;
  border: 1px solid #30363d;
  border-radius: 8px 8px 0 0;
  position: relative;
  z-index: 20;
  overflow: visible;
}

.idi-chart__title-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.idi-chart__title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  font-size: 15px;
}

.symbol {
  color: #f0f6fc;
}

.tf-tag {
  background: #21262d;
  color: #58a6ff;
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid #30363d;
}

.chart-type-tag {
  background: rgba(46, 160, 67, 0.15);
  color: #3fb950;
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid rgba(46, 160, 67, 0.4);
}

.idi-chart__price-display {
  font-size: 14px;
  display: flex;
  gap: 6px;
  background: #21262d;
  padding: 3px 8px;
  border-radius: 4px;
}

.idi-chart__price-display .lbl {
  color: #8b949e;
}

.idi-chart__price-display .val {
  color: #58a6ff;
  font-weight: 700;
}

.idi-chart__controls {
  display: flex;
  gap: 8px;
  align-items: center;
}

.idi-chart__profile-slot {
  display: flex;
  gap: 8px;
  align-items: center;
}

.idi-btn {
  background: #21262d;
  color: #c9d1d9;
  border: 1px solid #30363d;
  border-radius: 6px;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.15s ease;
}

.idi-btn:hover {
  background: #30363d;
  border-color: #8b949e;
}

.idi-btn--ws-active {
  background: rgba(88, 166, 255, 0.15);
  color: #58a6ff;
  border-color: #388bfd;
}

.ws-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #8b949e;
}

.idi-btn--ws-active .ws-dot {
  background: #3fb950;
  box-shadow: 0 0 6px #3fb950;
}

.idi-chart__wrapper {
  flex: 1;
  position: relative;
  overflow: hidden;
  border: 1px solid #30363d;
  border-top: none;
  border-radius: 0 0 8px 8px;
  background: #161b22;
}

.idi-chart__canvas {
  width: 100%;
  height: 100%;
}
</style>
